import { useState, useEffect, useRef } from "react";
import { User, Mail, Key, ShieldCheck, CheckCircle2, AlertCircle } from "lucide-react";
import api from "../../../api";
import { useAuth } from "../../../context/AuthProvider";
import { useModal } from "../../../context/ModalContext";
import { useNotification } from "../../../context/NotificationContext";
import { motion, AnimatePresence } from "framer-motion";

const getImageUrl = (path) => {
  if (!path) return null;
  if (path.startsWith('http') || path.startsWith('data:image')) return path;
  return path.startsWith('/') ? path : `/${path}`;
};

export default function Profil() {
  const { user } = useAuth();
  const { confirm } = useModal();
  const { showToast } = useNotification();
  const fileInputRef = useRef(null);
  
  const displayRole = user?.jobTitle || user?.Rôle || user?.roleNames?.[0] || "Utilisateur";
  
  const [formData, setFormData] = useState({
    prenom: "",
    nom: "",
    email: "",
  });

  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
  });

  const [status, setStatus] = useState({ type: null, message: "" });
  const [loading, setLoading] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState(null);

  // 2FA state
  const [is2FAEnabled, setIs2FAEnabled] = useState(false);
  const [qrCode, setQrCode] = useState(null);
  const [twoFactorCode, setTwoFactorCode] = useState("");

  useEffect(() => {
    // Extraire le prénom et le nom si on a seulement `Nom` (qui contient souvent le nom complet)
    let prenomStr = "";
    let nomStr = user?.Nom || "";
    
    if (user?.Nom?.includes(" ")) {
      const parts = user.Nom.split(" ");
      prenomStr = parts[0];
      nomStr = parts.slice(1).join(" ");
    }

    setFormData({
      prenom: prenomStr,
      nom: nomStr,
      email: user?.Email || "",
    });
    if (user?.profileImage) {
      setAvatarUrl(getImageUrl(user.profileImage));
    }

    const fetchProfile = async () => {
      try {
        const res = await api.post("/utilisateurs/getUser"); // l'API existante getUser (post)
        if (res.data.isTwoFactorEnabled) {
          setIs2FAEnabled(true);
        }
      } catch (err) {
        console.error("Failed to fetch profile details", err);
      }
    };
    if (user) fetchProfile();
  }, [user]);

  const showStatus = (type, message) => {
    setStatus({ type, message });
    setTimeout(() => setStatus({ type: null, message: "" }), 5000);
  };

  const handleProfileSubmit = async () => {
    setLoading(true);
    try {
      const fullName = `${formData.prenom} ${formData.nom}`.trim();
      await api.put("/utilisateurs/me", {
        Nom: fullName,
        Email: formData.email
      });
      showStatus("success", "Profil mis à jour avec succès.");
      // Optional: reload to refresh context
      setTimeout(() => window.location.reload(), 1000);
    } catch (err) {
      showStatus("error", "Erreur lors de la mise à jour du profil.");
    } finally {
      setLoading(false);
    }
  };

  const handleAvatarChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const data = new FormData();
    data.append("avatar", file);

    try {
      setLoading(true);
      const res = await api.put("/utilisateurs/me/avatar", data);
      showStatus("success", "Avatar mis à jour avec succès.");
      if (res.data.profileImage) {
        setAvatarUrl(getImageUrl(res.data.profileImage));
        setTimeout(() => window.location.reload(), 1000);
      }
    } catch (err) {
      showStatus("error", "Erreur lors de la mise à jour de l'avatar.");
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordSubmit = async () => {
    if (!passwordData.currentPassword || !passwordData.newPassword) {
      showToast("Veuillez remplir les deux champs de mot de passe.", "error");
      return;
    }
    
    // Check complexity
    const rules = [
      passwordData.newPassword.length >= 10,
      /[A-Z]/.test(passwordData.newPassword),
      /[0-9]/.test(passwordData.newPassword),
      /[^A-Za-z0-9]/.test(passwordData.newPassword)
    ];
    if (!rules.every(Boolean)) {
      showToast("Le mot de passe ne respecte pas les critères de sécurité.", "error");
      return;
    }

    const isConfirmed = await confirm({
      title: "Modifier le mot de passe",
      message: "Êtes-vous sûr de vouloir modifier votre mot de passe ? Vous serez déconnecté de vos autres sessions si elles ne sont pas à jour.",
      type: "warning",
      confirmText: "Modifier le mot de passe",
      cancelText: "Annuler"
    });
    if (!isConfirmed) return;

    setLoading(true);
    try {
      await api.put("/utilisateurs/me/password", passwordData);
      showToast("Mot de passe modifié avec succès.", "success");
      setPasswordData({ currentPassword: "", newPassword: "" });
    } catch (err) {
      showToast(err.response?.data?.message || "Erreur lors du changement de mot de passe.", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleSetup2FA = async () => {
    setLoading(true);
    try {
      const res = await api.post("/utilisateurs/me/2fa/setup");
      setQrCode(res.data.qrCode);
    } catch (err) {
      showStatus("error", "Erreur lors de la configuration 2FA.");
    } finally {
      setLoading(false);
    }
  };

  const handleVerify2FA = async () => {
    setLoading(true);
    try {
      await api.post("/utilisateurs/me/2fa/verify", { token: twoFactorCode });
      showStatus("success", "Authentification 2FA activée avec succès !");
      setIs2FAEnabled(true);
      setQrCode(null);
    } catch (err) {
      showStatus("error", "Code 2FA invalide.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 h-full flex flex-col gap-6 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-transparent">
      <div className="mb-2">
        <h2 className="text-2xl font-bold text-primary flex items-center justify-between">
          <span>Mon Profil</span>
        </h2>
        <p className="text-sm text-subtle mt-1">Gérez vos informations personnelles et paramètres de sécurité.</p>
      </div>

      <div className="relative workspace-card p-6 overflow-hidden">
        <AnimatePresence>
          {status.type && (
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 z-10 flex items-center justify-center bg-surface/60 backdrop-blur-[2px]"
            >
              <div className={`px-4 py-3 rounded-xl font-semibold flex items-center gap-2 shadow-hp-md ${status.type === 'success' ? 'bg-[var(--hp-success-50)] text-[var(--hp-success-700)] border border-[var(--hp-success-200)]' : 'bg-[var(--hp-danger-50)] text-[var(--hp-danger-700)] border border-[var(--hp-danger-200)]'}`}>
                {status.type === 'success' ? <CheckCircle2 className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
                {status.message}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <h3 className="text-lg font-semibold text-primary mb-4 flex items-center gap-2">
          <User className="w-5 h-5 text-secondary" />
          Informations Personnelles
        </h3>
        
        <div className="flex flex-col md:flex-row gap-6 mb-2 items-center md:items-start">
          <div 
            onClick={() => fileInputRef.current?.click()}
            className="w-24 h-24 rounded-full bg-surface-muted flex items-center justify-center text-3xl font-bold text-muted border-2 border-workspace shrink-0 cursor-pointer overflow-hidden relative group hover:border-primary transition-colors"
          >
            {avatarUrl ? (
              <img src={avatarUrl} alt="Avatar" className="w-full h-full object-cover" />
            ) : (
              <>{formData.prenom[0] || ""}{formData.nom[0] || ""}</>
            )}
            <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
              <span className="text-white text-xs font-medium">Modifier</span>
            </div>
          </div>
          <input 
            type="file" 
            ref={fileInputRef} 
            onChange={handleAvatarChange} 
            className="hidden" 
            accept="image/jpeg, image/png, image/webp" 
          />
          <div className="flex-1 w-full space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-primary mb-1">Prénom</label>
                <input 
                  type="text" 
                  className="workspace-input w-full"
                  value={formData.prenom}
                  onChange={(e) => setFormData({...formData, prenom: e.target.value})}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-primary mb-1">Nom</label>
                <input 
                  type="text" 
                  className="workspace-input w-full"
                  value={formData.nom}
                  onChange={(e) => setFormData({...formData, nom: e.target.value})}
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-primary mb-1">Adresse Email</label>
              <div className="relative">
                <Mail className="w-4 h-4 absolute left-3 top-3 text-muted" />
                <input 
                  type="email" 
                  className="workspace-input pl-10 w-full"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                />
              </div>
            </div>
            <div className="flex justify-end pt-2">
              <button onClick={handleProfileSubmit} disabled={loading} className="btn btn-primary">
                {loading ? "Enregistrement..." : "Enregistrer les modifications"}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="workspace-card p-6">
          <h3 className="text-lg font-semibold text-primary mb-4 flex items-center gap-2">
            <Key className="w-5 h-5 text-info-500" />
            Mot de passe
          </h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-primary mb-1">Mot de passe actuel</label>
              <input 
                type="password" 
                className="workspace-input w-full" 
                placeholder="••••••••" 
                value={passwordData.currentPassword}
                onChange={(e) => setPasswordData({...passwordData, currentPassword: e.target.value})}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-primary mb-1">Nouveau mot de passe</label>
              {(() => {
                const rules = [
                  { label: "10 caractères minimum", regex: /.{10,}/ },
                  { label: "Au moins une majuscule", regex: /[A-Z]/ },
                  { label: "Au moins un chiffre", regex: /[0-9]/ },
                  { label: "Au moins un caractère spécial", regex: /[^A-Za-z0-9]/ }
                ];
                const isDirty = passwordData.newPassword.length > 0;
                const isValid = isDirty && rules.every(r => r.regex.test(passwordData.newPassword));
                const inputClass = isDirty && !isValid 
                  ? "workspace-input w-full border-[var(--hp-danger-500)] text-[var(--hp-danger-600)] focus:border-[var(--hp-danger-500)] focus:ring-[var(--hp-danger-500)]"
                  : "workspace-input w-full";

                return (
                  <>
                    <input 
                      type="password" 
                      className={inputClass}
                      placeholder="••••••••" 
                      value={passwordData.newPassword}
                      onChange={(e) => setPasswordData({...passwordData, newPassword: e.target.value})}
                    />
                    
                    {/* Indicateur de complexité */}
                    {isDirty && (
                      <div className="mt-3 space-y-2">
                        <p className={`text-xs font-semibold ${isValid ? 'text-[var(--hp-success-600)]' : 'text-[var(--hp-danger-600)]'}`}>Critères requis :</p>
                        <ul className="text-xs space-y-1">
                          {rules.map((crit, idx) => {
                            const isMet = crit.regex.test(passwordData.newPassword);
                            return (
                              <li key={idx} className={`flex items-center gap-1.5 ${isMet ? 'text-[var(--hp-success-600)] dark:text-success-400' : 'text-[var(--hp-danger-500)]'}`}>
                                {isMet ? <CheckCircle2 className="w-3.5 h-3.5" /> : <div className="w-3.5 h-3.5 border border-current rounded-full" />}
                                {crit.label}
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
            <button onClick={handlePasswordSubmit} disabled={loading} className="btn btn-secondary w-full justify-center">
              Mettre à jour le mot de passe
            </button>
          </div>
        </div>

        <div className="workspace-card p-6">
          <h3 className="text-lg font-semibold text-primary mb-4 flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-success-500" />
            Authentification (2FA)
          </h3>
          <p className="text-sm text-subtle mb-4">
            Protégez votre compte en ajoutant une couche de sécurité supplémentaire.
          </p>
          
          {is2FAEnabled ? (
            <div className="bg-success-50 dark:bg-success-900/20 text-success-800 dark:text-success-200 p-4 rounded-lg mb-4 border border-success-100 dark:border-success-800/50">
              <p className="text-sm font-medium flex items-center gap-2">
                <ShieldCheck className="w-4 h-4" />
                2FA est actuellement activée
              </p>
            </div>
          ) : (
            <>
              <div className="bg-orange-50 dark:bg-orange-900/20 text-orange-800 dark:text-orange-200 p-4 rounded-lg mb-4 border border-orange-100 dark:border-orange-800/50">
                <p className="text-sm font-medium flex items-center gap-2">
                  <AlertCircle className="w-4 h-4" />
                  2FA actuellement désactivée
                </p>
              </div>
              
              {!qrCode ? (
                <button onClick={handleSetup2FA} disabled={loading} className="btn btn-primary w-full justify-center">
                  Configurer la 2FA
                </button>
              ) : (
                <div className="flex flex-col items-center space-y-4">
                  <p className="text-sm font-medium text-center text-primary">1. Scannez ce QR Code avec Google Authenticator ou Authy</p>
                  <img src={qrCode} alt="QR Code 2FA" className="w-40 h-40 border border-workspace rounded-lg p-2 bg-white" />
                  
                  <p className="text-sm font-medium text-center text-primary w-full text-left mt-2">2. Entrez le code généré</p>
                  <div className="flex gap-2 w-full">
                    <input 
                      type="text" 
                      maxLength="6"
                      placeholder="000000"
                      className="workspace-input flex-1 text-center font-mono tracking-widest text-lg"
                      value={twoFactorCode}
                      onChange={(e) => setTwoFactorCode(e.target.value)}
                    />
                    <button onClick={handleVerify2FA} disabled={loading || twoFactorCode.length !== 6} className="btn btn-primary whitespace-nowrap">
                      Vérifier
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
